import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Title } from 'components/Title';
import { allCapsStyle } from 'components/AllCaps/AllCaps';
import { boxShadow3 } from '../../design-system/style-mixins/shadows-and-scrims';
import {
	black,
	blackHover,
	disabled,
	dividerGrayLight,
	error,
	focusOutlineColor,
	gray,
	grayMedium,
	paleBlue,
	tapTransparent,
	textColorDisabled,
	textColorMedium,
	white,
} from '../../design-system/variables/colors';
import { spaceBase, spaceLarge, spaceSmall, spaceMedium } from '../../design-system/variables/spaces';
import { xsOnly } from '../../design-system/media-queries';
import { fontSans, textBase, textSmall, textTiny } from '../../design-system/style-mixins/text';

export const Container = styled.div<{ formIsLoaded: boolean }>`
	visibility: ${props => (props.formIsLoaded ? 'visible' : 'hidden')};
	display: block;
	width: 100%;
	${boxShadow3}
	background-color: ${white};
	padding: ${spaceLarge};
	text-align: center;

	${xsOnly`
		margin-top: ${spaceLarge};
		min-width: auto;
		padding: ${spaceBase};
	`}

	.dcwdw_widget {
		padding-left: 0;
		padding-right: 0;
		/* text-align: left; */

		.dcwdw_field_pickupTime,
		.dcwdw_field_pickupDate,
		.dcwdw_field_dropoffTime,
		.dcwdw_field_dropoffDate {
			width: 48% !important;
		}

		button, button:focus {
			${allCapsStyle}
			box-sizing: border-box;
			cursor: pointer;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex-wrap: wrap;
			outline: none;
			text-align: center;
			text-decoration: none;
			transition: background-color 100ms linear;
			user-select: none;
			border-radius: 0;
			float: none;
			max-width: 240px;
			/* min-width: 55%; */
			width: 100%;
			line-height: 16px;

			background: ${black};
			border: 1px solid ${black};
			margin: ${spaceSmall} auto 0 auto;
			${tapTransparent}
			${textTiny}
			color: ${white};

			&:hover {
				text-decoration: none;
				background: ${blackHover};
				border: 1px solid ${blackHover};
				color: ${white};
			}
			&:active {
				text-decoration: none;
				transition: background-color 0ms linear;
				background: ${black};
				color: ${white};
			}
			&[data-focus-visible-added] {
				outline: 2px solid ${focusOutlineColor};
				outline-offset: 2px;
			}

			&:visited {
				color: ${white};

				&:hover {
					color: ${white};
				}
			}
		}

		.dcwdw_field_countryResidence,
		.dcwdw_field_dropoffDate {
			clear: left;
		}

		.dcwdw_field {
			text-align: left;
			display: none;
			input[type='email'],
			input[type='search'],
			input[type='text'],
			input[type='tel'],
			input[type='url'],
			input[type='password'],
			textarea,
			select {
				${textBase}
				${fontSans}
				background-color: ${white};
				border: 1px solid ${gray};
				line-height: 1.5;
				margin-bottom: ${spaceBase};

				&:focus {
					border-color: ${grayMedium};
					outline: none;
				}
			}

			.dcwdw_typeahead {
				background-color: ${white};
				border: 1px solid ${grayMedium};
				border-radius: 0;
				border-top: none;
				margin-top: -19px;
				max-height: 225px;
				overflow-x: hidden;
				overflow-y: auto;
				width: 100% !important;

				&:empty {
					display: none;
				}
			}

			.dcwdw_typeahead_result {
				${textBase}
				${textColorMedium}
				line-height: 1.5;
				padding-bottom: ${spaceSmall};
				padding-top: ${spaceSmall};
				padding-left: ${spaceSmall};

				&:hover {
					/* ${fontSans} */
					background-color: ${black};
					color: ${white};
				}
			}

			label {
				line-height: 18px;
				font-weight: normal;
				margin-bottom: ${spaceSmall};
				white-space: nowrap;
				${fontSans}
				${textColorMedium}
				${textBase}
			}

			span {
				display: block;
				padding-left: 0;
			}

			input[type='checkbox'] {
				margin-top: 3px;
				vertical-align: top;
			}

			input[type='checkbox'] + span {
				${textSmall}
				${fontSans}
				display: inline-block;
				line-height: 1.5;
				margin-bottom: ${spaceBase};
				text-transform: initial;
				white-space: normal;
				width: calc(100% - 21px);
			}
		}
	}

	.dcwdw_error_message {
		${textSmall}
		color: ${error};
		font-weight: normal;
	}

	.dcwdw_field_checkbox label {
		margin-top: -${spaceSmall};
	}

	select {
		color: ${black};
	}

	.dcwdw_field_pickupDate input[type='text'],
	.dcwdw_field_dropoffDate input[type='text'] {
		background-image: url(data:image/svg+xml;base64,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);
		background-position: 5px 5px;
		background-repeat: no-repeat;
		padding-left: ${spaceLarge};
		${xsOnly`
			background-image: none;
			padding-left: ${spaceSmall};
		`}
	}

	.dcwdw_field_wizardNumber {
		clear: left;
	}

	.dcwdw_field_pickupDate input[type='text'],
	.dcwdw_field_dropoffDate input[type='text']{
		color: ${black};
	}

	.dcwdw_field_pickupLocation,
	.dcwdw_field_dropoffLocation,
	.dcwdw_field_checkbox,
	.dcwdw_field_pickupDate,
	.dcwdw_field_pickupTime,
	.dcwdw_field_dropoffDate,
	.dcwdw_field_dropoffTime,
	.dcwdw_field_countryResidence,
	.dcwdw_field_wizardNumber {
		display: block !important;
	}

	/**
	 * 1. Remove fields from the form
	 */
	.dcwdw_widget_avis_logo,
	.dcwdw_air_icon,
	.dcwdw_dual_icon,
	.dcwdw_avis_icon,
	.dcwdw_budget_icon,
	.dcwdw_car_icon {
		display: none !important; /* 1 */
	}
`;

export const Styles = createGlobalStyle`
	/**
	 * 1. Prevent the calendar from living off the screen on really small breakpoints
	 */
	.dcwdw_pika-single.is-bound {
		${fontSans}
		${textColorMedium}
		@media screen and (max-width: 414px) {
			left: 50% !important;
			margin-left: -149px !important; /* 1 */
		}
		.pika-button {
			${textSmall}
			background: ${white};
			border-bottom: ${dividerGrayLight};
			height: 40px;
			opacity: 1;
			text-align: center;
			width: 40px;

			&:hover {
				background: ${paleBlue} !important;
				border-radius: 0 !important;
				color: ${black} !important;
			}
		}
		.pika-lendar {
			width: inherit;
		}
		.is-disabled .pika-button {
			background: ${disabled};
			${textColorDisabled};
		}
		.is-selected .pika-button {
			background: ${black};
			border-radius: 0;
		}
		.pika-next {
			background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pgo8IS0tIEdlbmVyYXRvcjogQWRvYmUgSWxsdXN0cmF0b3IgMTkuMC4wLCBTVkcgRXhwb3J0IFBsdWctSW4gLiBTVkcgVmVyc2lvbjogNi4wMCBCdWlsZCAwKSAgLS0+CjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeD0iMHB4IiB5PSIwcHgiIHZpZXdCb3g9IjAgMCA0NTUgNDU1IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCA0NTUgNDU1OyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSIgd2lkdGg9IjUxMnB4IiBoZWlnaHQ9IjUxMnB4Ij4KPHBhdGggZD0iTTIyNy41LDBDMTAxLjg1NSwwLDAsMTAxLjg1NSwwLDIyNy41UzEwMS44NTUsNDU1LDIyNy41LDQ1NVM0NTUsMzUzLjE0NSw0NTUsMjI3LjVTMzUzLjE0NSwwLDIyNy41LDB6IE0xOTkuNDc2LDM1NS41ODkgIGwtMjEuMjQ4LTIxLjE3OEwyODQuNzkxLDIyNy41TDE3OC4yMjgsMTIwLjU4OWwyMS4yNDgtMjEuMTc4TDMyNy4xNDgsMjI3LjVMMTk5LjQ3NiwzNTUuNTg5eiIgZmlsbD0iIzY1NjU2NSIvPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8L3N2Zz4K');
		}
		.pika-prev {
			background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pgo8IS0tIEdlbmVyYXRvcjogQWRvYmUgSWxsdXN0cmF0b3IgMTkuMC4wLCBTVkcgRXhwb3J0IFBsdWctSW4gLiBTVkcgVmVyc2lvbjogNi4wMCBCdWlsZCAwKSAgLS0+CjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeD0iMHB4IiB5PSIwcHgiIHZpZXdCb3g9IjAgMCA0NTUgNDU1IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCA0NTUgNDU1OyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSIgd2lkdGg9IjUxMnB4IiBoZWlnaHQ9IjUxMnB4Ij4KPHBhdGggZD0iTTIyNy41LDBDMTAxLjg1NSwwLDAsMTAxLjg1NSwwLDIyNy41UzEwMS44NTUsNDU1LDIyNy41LDQ1NVM0NTUsMzUzLjE0NSw0NTUsMjI3LjVTMzUzLjE0NSwwLDIyNy41LDB6IE0yNzYuNzcyLDMzNC40MTEgIGwtMjEuMjQ4LDIxLjE3OEwxMjcuODUyLDIyNy41TDI1NS41MjQsOTkuNDExbDIxLjI0OCwyMS4xNzhMMTcwLjIwOSwyMjcuNUwyNzYuNzcyLDMzNC40MTF6IiBmaWxsPSIjNjU2NTY1Ii8+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+CjxnPgo8L2c+Cjwvc3ZnPgo=');
		}
		.pika-title button {
			height: 30px;
			opacity: 1;
			padding-left: ${spaceMedium};
			width: 30px;

			&:hover {
				opacity: 0.9;
			}
		}

		.pika-title button.is-disabled {
			opacity: 0.5;
		}

		.pika-title .pika-label {
			${textBase}
		}

		abbr[title] {
			${textSmall}
			color: ${black};
			display: inherit;
			padding-left: 13px;
			text-decoration: none;
			visibility: hidden;

		&::first-letter {
				visibility: visible;
			}
		}
	}
`;

export const Headline = styled(props => <Title {...props} />)`
	margin-bottom: ${spaceSmall};
`;
